import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from '../components/Sidebar.jsx'
import TopBar from '../components/TopBar.jsx'
import ErrorBoundary from '../components/ErrorBoundary.jsx'

const pageTitles = {
  '/': 'Dashboard',
  '/users': 'Users',
  '/vehicles': 'Vehicles',
  '/system': 'System Health',
}

const getSystemDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-color-scheme: dark)').matches

const AdminLayout = () => {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('admin_sidebarCollapsed')) || false
    } catch { return false }
  })

  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window === 'undefined') return 'system'
    try {
      const stored = window.localStorage.getItem('admin_themeMode')
      if (stored && ['light', 'system', 'dark'].includes(stored)) return stored
      // Migrate legacy darkMode flag
      const legacy = window.localStorage.getItem('admin_darkMode')
      if (legacy !== null) return JSON.parse(legacy) ? 'dark' : 'light'
    } catch {}
    return 'system'
  })

  const [systemDark, setSystemDark] = useState(getSystemDark)

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq) return
    const handler = (e) => setSystemDark(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const darkMode = themeMode === 'dark' || (themeMode === 'system' && systemDark)

  useEffect(() => {
    const root = document.documentElement
    if (darkMode) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    try {
      window.localStorage.setItem('admin_themeMode', themeMode)
      window.localStorage.removeItem('admin_darkMode')
    } catch {}
  }, [darkMode, themeMode])

  useEffect(() => {
    try { localStorage.setItem('admin_sidebarCollapsed', JSON.stringify(collapsed)) } catch {}
  }, [collapsed])

  const title = pageTitles[location.pathname] ||
    (location.pathname.includes('/users/') && location.pathname.includes('/vehicles') ? 'User Vehicles' : 'CarFile Admin')

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-500 focus:text-white focus:rounded-lg">
        Skip to content
      </a>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className={`transition-all duration-300 ${collapsed ? 'ml-20' : 'ml-64'}`}>
        <TopBar themeMode={themeMode} setThemeMode={setThemeMode} darkMode={darkMode} title={title} />
        <main id="main-content" className="p-4 sm:p-6">
          <ErrorBoundary>
            <Outlet context={{ darkMode }} />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
